import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Bot, Lock, Sparkles } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useOpenRaid } from '@/lib/raid';
import type { Program } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Field, Textarea } from '@/components/ui/field';
import { Badge, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { useOpenTask } from '@/components/tracker/TaskPanel';

interface AiStatus {
  available: boolean;
  reason: { code: string; message: string } | null;
  tiersApproved: number[];
}

export function useAiStatus(program: Program) {
  return useQuery({
    queryKey: ['ai', program.id, 'status'],
    queryFn: () => api.get<AiStatus>(`/api/programs/${program.id}/ai/status`),
    staleTime: 60_000,
  });
}

/**
 * Whether to show assistant controls to this member at all: BlackWolf always (so the
 * "Awaiting approval" state is visible), client roles only once an approval allows them.
 */
export function useAiVisible(program: Program) {
  const s = useAiStatus(program);
  return program.membership.role === 'ADMIN' || !!s.data?.available;
}

/** An assistant button that shows the gate plainly instead of calling anything. */
export function AiButton({
  program,
  children,
  onClick,
  loading,
  size = 'sm',
  tier = 1,
  disabled,
}: {
  program: Program;
  children: ReactNode;
  onClick: () => void;
  loading?: boolean;
  size?: 'sm' | 'md';
  /** Data tier the action sends; the approval must cover it. */
  tier?: 1 | 2 | 3;
  disabled?: boolean;
}) {
  const s = useAiStatus(program);
  if (!useAiVisible(program)) return null;
  const tierOk = (s.data?.tiersApproved ?? []).includes(tier);
  const ok = !!s.data?.available && tierOk;
  const reason =
    s.data?.reason ?? (s.data && !tierOk ? { message: `Awaiting ${program.clientShortName} approval` } : null);
  return (
    <Button
      variant="secondary"
      size={size}
      onClick={onClick}
      loading={loading}
      disabled={!ok || disabled}
      title={ok ? undefined : reason?.message}
    >
      {ok ? <Sparkles /> : <Lock />}
      {children}
      {!ok && reason && <span className="text-xs font-normal text-muted">({reason.message})</span>}
    </Button>
  );
}

export const AiBadge = ({ runId }: { runId?: string | null }) =>
  runId ? (
    <Badge tone="brand" title={`Accepted from assistant run ${runId}`}>
      <Sparkles className="mr-1 inline size-3" /> AI-assisted
    </Badge>
  ) : null;

export function useAssisted(program: Program, ids: string[]) {
  return useQuery({
    queryKey: ['ai', program.id, 'assisted', ids.join(',')],
    enabled: ids.length > 0,
    queryFn: () => api.get<Record<string, string>>(`/api/programs/${program.id}/ai/assisted?ids=${ids.join(',')}`),
  });
}

interface Finding {
  title: string;
  detail: string;
  recordId: string | null;
}
interface Review {
  summary: string;
  slippage: Finding[];
  staleTasks: Finding[];
  dateIssues: Finding[];
  deliverablesWithoutTasks: Finding[];
  risksWithoutMitigation: Finding[];
  focusThisWeek: string[];
}
const SECTIONS: Array<[keyof Review, string]> = [
  ['slippage', 'Slippage'],
  ['staleTasks', 'Stale tasks'],
  ['dateIssues', 'Inconsistent dates'],
  ['deliverablesWithoutTasks', 'Deliverables with no tasks'],
  ['risksWithoutMitigation', 'Risks with no mitigation'],
];

/** Progress review for a phase or the program. Read-only findings; nothing changes. */
export function ProgressReview({ program, phaseId, label }: { program: Program; phaseId?: string; label: string }) {
  const toast = useToast();
  const [result, setResult] = useState<Review | null>(null);
  const run = useMutation({
    mutationFn: () =>
      api.post<{ review: Review }>(`/api/programs/${program.id}/ai/progress-review`, phaseId ? { phaseId } : {}),
    onSuccess: (r) => setResult(r.review),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <>
      <AiButton program={program} onClick={() => run.mutate()} loading={run.isPending}>
        {label}
      </AiButton>
      <Dialog
        open={!!result}
        onClose={() => setResult(null)}
        wide
        title="Progress review"
        description="A draft from the assistant. Check each point against the plan; nothing has been changed."
      >
        {result && (
          <div className="space-y-4 text-sm">
            <p>{result.summary}</p>
            {SECTIONS.map(([k, title]) => {
              const items = result[k] as Finding[];
              return items.length ? (
                <div key={k}>
                  <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">{title}</h3>
                  <ul className="space-y-1">
                    {items.map((f, i) => (
                      <li key={i}>
                        <span className="font-medium">{f.title}</span>: {f.detail}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null;
            })}
            {result.focusThisWeek.length > 0 && (
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Focus this week</h3>
                <ol className="list-decimal space-y-1 pl-5">
                  {result.focusThisWeek.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
}

/** "Ask about the program": an answer with links to the records it used. */
export function AskPanel({ program }: { program: Program }) {
  const toast = useToast();
  const openTask = useOpenTask();
  const openRaid = useOpenRaid();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ask = useMutation({
    mutationFn: () =>
      api.post<{ answer: string; sources: Array<{ type: string; id: string; label: string }> }>(
        `/api/programs/${program.id}/ai/ask`,
        { question: q },
      ),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const visible = useAiVisible(program);
  const status = useAiStatus(program);
  if (!visible) return null;
  const link = (s: { type: string; id: string; label: string }) => {
    if (s.type === 'Task')
      return (
        <button type="button" className="text-brand-text hover:underline" onClick={() => openTask(s.id)}>
          {s.label}
        </button>
      );
    if (s.type === 'RaidItem')
      return (
        <button type="button" className="text-brand-text hover:underline" onClick={() => openRaid(s.id)}>
          {s.label}
        </button>
      );
    const to = s.type === 'Phase' ? `/p/${program.id}/tracker/phase/${s.id}` : `/p/${program.id}/deliverables/${s.id}`;
    return (
      <Link to={to} className="text-brand-text hover:underline" onClick={() => setOpen(false)}>
        {s.label}
      </Link>
    );
  };

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} aria-label="Ask the assistant">
        <Bot /> <span className="hidden lg:inline">Ask</span>
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Ask about the program"
        description="Answers come from the plan, deliverables and RAID log you can see. Program data only."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => ask.mutate()}
              loading={ask.isPending}
              disabled={!status.data?.available || q.trim().length < 3}
              title={status.data?.available ? undefined : status.data?.reason?.message}
            >
              <Sparkles /> Ask
            </Button>
          </>
        }
      >
        {!status.data?.available && status.data?.reason && (
          <p className="mb-3 flex items-center gap-2 text-sm text-muted">
            <Lock className="size-4" /> {status.data.reason.message}
          </p>
        )}
        <Field label="Question" htmlFor="ask-q">
          <Textarea
            id="ask-q"
            rows={2}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Which Phase 1 deliverables are at risk of missing November 30?"
          />
        </Field>
        {ask.isPending && <Spinner className="h-16" />}
        {ask.data && (
          <div className="mt-4 space-y-2 text-sm">
            <p>{ask.data.answer}</p>
            {ask.data.sources.length > 0 && (
              <div className="text-xs text-muted">
                Based on:{' '}
                {ask.data.sources.map((s, i) => (
                  <span key={s.id}>
                    {i > 0 && ', '}
                    {link(s)}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
}
